import type { IRun, IUser, IRunAnalytics } from './types';

import * as ActionTypes from 'src/state/action-types';

export interface IRunState {
  loading: boolean;
  currentRun: IRun | null;
  error: Error | null;
  analytics: IRunAnalytics | null;
}

export const initialState: IRunState = {
  loading: false,
  currentRun: null,
  error: null,
  analytics: null,
};

export default function runReducer(
  state: IRunState = initialState,
  action: ActionTypes.ActionTypes
): IRunState {
  switch (action.type) {
    case ActionTypes.RUN_CREATE_UPDATE_REQUEST:
    case ActionTypes.RUN_GET_REQUEST:
    case ActionTypes.RUN_GET_ANALYTICS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ActionTypes.RUN_CREATE_UPDATE_SUCCESS:
      return {
        ...state,
        loading: false,
        currentRun: action.payload.run,
        error: null,
      };

    case ActionTypes.RUN_GET_SUCCESS:
      return {
        ...state,
        loading: false,
        currentRun: action.payload.run,
        error: null,
      };

    case ActionTypes.RUN_GET_ANALYTICS_SUCCESS:
      return {
        ...state,
        loading: false,
        analytics: {
          users: action.payload.users,
          answers: action.payload.answers,
          interactions: action.payload.interactions,
          contentId: action.payload.contentId,
        },
        error: null,
      };

    case ActionTypes.RUN_CREATE_UPDATE_FAILURE:
    case ActionTypes.RUN_GET_FAILURE:
    case ActionTypes.RUN_GET_ANALYTICS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload as Error,
      };

    case ActionTypes.RUN_UPDATE_PAUSED:
      return {
        ...state,
        currentRun: state.currentRun
          ? {
              ...state.currentRun,
              paused: action.payload.paused,
            }
          : null,
      };

    case ActionTypes.RUN_XAPI_STATEMENT_RECEIVED: {
      if (!state.analytics) return state;

      const { userId, name, statement } = action.payload;
      const existingUserIndex = state.analytics.users.findIndex((u: IUser) => u.id === userId);

      const updatedUsers = [...state.analytics.users];

      if (existingUserIndex === -1) {
        // User doesn't exist, add them
        updatedUsers.push({ id: userId, name: name || '' });
      } else if (name && !updatedUsers[existingUserIndex].name) {
        // User exists but has no name, update with provided name
        updatedUsers[existingUserIndex] = {
          ...updatedUsers[existingUserIndex],
          name,
        };
      }

      const updatedAnswers = [...state.analytics.answers];

      // Add answer if this is an answered statement
      if (
        statement?.verb?.id === 'http://adlnet.gov/expapi/verbs/answered' &&
        statement?.result?.score?.scaled !== undefined
      ) {
        updatedAnswers.push({
          userId,
          interactionId: statement.object.id,
          score: statement.result.score.scaled,
          active: false,
          completed: true,
        });
      }

      return {
        ...state,
        analytics: {
          ...state.analytics,
          users: updatedUsers,
          answers: updatedAnswers,
        },
      };
    }

    case ActionTypes.RUN_ADD_USER: {
      if (!state.analytics) return state;

      const existingUser = state.analytics.users.find(
        (u: IUser) => u.id === action.payload.user.id
      );
      if (existingUser) return state;

      return {
        ...state,
        analytics: {
          ...state.analytics,
          users: [...state.analytics.users, action.payload.user],
        },
      };
    }

    case ActionTypes.RUN_UPDATE_USER: {
      if (!state.analytics) return state;

      const userIndex = state.analytics.users.findIndex(
        (u: IUser) => u.id === action.payload.userId
      );
      if (userIndex === -1) return state;

      const updatedUsers = [...state.analytics.users];
      updatedUsers[userIndex] = {
        ...updatedUsers[userIndex],
        name: action.payload.name,
      };

      return {
        ...state,
        analytics: {
          ...state.analytics,
          users: updatedUsers,
        },
      };
    }

    case ActionTypes.RUN_ADD_ANSWER: {
      if (!state.analytics) return state;

      return {
        ...state,
        analytics: {
          ...state.analytics,
          answers: [...state.analytics.answers, action.payload],
        },
      };
    }

    case ActionTypes.AUTH_LOGOUT_REQUEST:
    case ActionTypes.USER_DELETE_ACCOUNT_SUCCESS:
      return initialState;

    default:
      return state;
  }
}
